import { DECIMAL_ZERO, decimalAdd, decimalCompare, decimalSchema, decimalSubtract, decimalSum, type DecimalValue } from "@shared/values/decimal";
import { ORDER_ACTION_VALUES } from "@shared/platform/domain-values";
import { CHANGE_HISTORY_FIELDS, CHANGE_HISTORY_TYPES } from "@shared/platform/audit-history";
import { z } from "zod";

import { DEFAULT_INITIAL_STOCK_TYPE, INITIAL_STOCK_TYPES, INITIAL_STOCK_TYPE_VALUES, INITIAL_STOCK_MESSAGES, initialStockIssues, parseInitialPurchaseDate } from "@shared/inventory/chemical-initial-stock";
import { API_PATHS } from "@shared/platform/app-metadata";
import { previewOrderImport } from "@shared/orders/order-import-contract";
import { latestOrderDatePerContact } from "@shared/contacts/contact-last-order-date";
import { demoDashboardResponse } from "@/static-demo/dashboard-fixtures";
import { demoOrderPriceResponse } from "@/static-demo/order-price-fixtures";
import { requestUrl, routePattern } from "@/static-demo/request-url";
import { attachMovedQuantities, type OrderTotalRow } from "@shared/orders/order-list-totals";
import { ORDER_HISTORY_EVENTS, type OrderHistoryEventType } from "@shared/orders/order-history-events";
import { defaultDeliverySchedule, deliveryScheduleSchema, orderQuantityOf, type DeliveryEntry } from "@shared/orders/order-delivery-schedule";
import { rejectLegacyOrderFields } from "@shared/orders/order-input-contract";
import { STATIC_DEMO_MESSAGES } from "@/lib/ui-labels";
import { getTransactionTypeMetadata, isTransactionType, TRANSACTION_STOCK_EFFECTS, TRANSACTION_VALIDATION_MESSAGES, type TransactionStockEffect, type TransactionType } from "@shared/inventory/transaction-types";
import {
  DEMO_ADDITIONAL_TENANT_USERS,
  DEMO_CHANGE_HISTORY_DEFINITIONS,
  DEMO_CHEMICAL_DEFINITIONS,
  DEMO_CONTACT_DEFINITIONS,
  DEMO_ORDER_DEFINITIONS,
  DEMO_PURCHASER_DEFINITION,
  DEMO_SUPPLIER_DEFINITION,
  DEMO_TRANSACTION_DEFINITIONS,
  DEMO_USER_DEFINITIONS,
  type DemoChemicalKey,
  type DemoContactKey,
  type DemoOrderDefinition,
  DEMO_CONTACT_PERSON,
  DEMO_CONTACT_PHONES,
} from "@shared/platform/demo-fixture-data";

import { chemicalEditInputSchema } from "@shared/inventory/chemical-field-policy";
import { editOrderSchema, firstOrderInputIssue, type SemanticOrderInput } from "@shared/orders/order-input-schemas";
import { canAmendOrderStatus } from "@shared/orders/order-amendment-policy";
import { ORDER_INPUT_COPY } from "@shared/orders/order-input-copy";
import { insertContactPersonSchema, insertContactSchema, insertInventoryTransactionSchema } from "@shared/platform/schema";
import { amendmentFieldsFor, transactionMetadataInputSchema } from "@shared/inventory/transaction-field-policy";
import { isReclassification, reclassificationIssues } from "@shared/inventory/transaction-reclassification";
import { ORDER_DIRECTIONS, ORDER_STATUSES, orderDirectionSchema, orderStatusSchema, type OrderDirection, type OrderStatus, ORDER_ACTIONS, type OrderAction } from "@shared/orders/order-types";

import { createDemoAddresses } from "./demo-addresses";
import { errorResponse, jsonResponse } from "./demo-responses";
import { CONTACT_TYPES, canActAsPurchaser, canActAsSupplier } from "@shared/contacts/contact-roles";
import { NOT_FOUND_MESSAGES } from "@shared/platform/api-copy";
import { transactionWorkflowSchema } from "@shared/inventory/transaction-workflow";
import { splitChemicalSchema, splitOverdrawIssue } from "@shared/inventory/batch-split-policy";

const DEMO_TENANT_ID = 1;
const now = () => new Date().toISOString();

const CHEMICAL_IDS: Record<DemoChemicalKey, number> = Object.fromEntries(
  (Object.keys(DEMO_CHEMICAL_DEFINITIONS) as DemoChemicalKey[]).map((key, index) => [key, index + 1]),
) as Record<DemoChemicalKey, number>;

const CONTACT_IDS: Record<DemoContactKey, number> = Object.fromEntries(
  DEMO_CONTACT_DEFINITIONS.map((contact) => [contact.key, contact.id]),
) as Record<DemoContactKey, number>;

const DEMO_GENERATION_CONFIG = { enabled: false };


function buildFixtureChemicals() {
  return (Object.keys(DEMO_CHEMICAL_DEFINITIONS) as DemoChemicalKey[]).map((key) => {
    const definition = DEMO_CHEMICAL_DEFINITIONS[key];
    return {
      id: CHEMICAL_IDS[key],
      productNumber: definition.productNumber,
      name: definition.name,
      casNumber: definition.casNumber,
      batchNumber: definition.batchNumber,
      location: definition.location,
      amount: definition.amount,
      unit: definition.unit,
      costPerUnit: definition.costPerUnit,
      modifiedBy: DEMO_USER_DEFINITIONS.admin.id,
      modifiedDate: now(),
      tenantId: DEMO_TENANT_ID,
      parentChemicalId: null,
      splitTransactionId: null,
    };
  });
}

function buildFixtureContacts() {
  return DEMO_CONTACT_DEFINITIONS.map((contact) => ({
    id: contact.id,
    company: contact.company,
    contactPerson: DEMO_CONTACT_PERSON,
    contactType: contact.contactType,
    email: contact.email,
    phone: DEMO_CONTACT_PHONES.primary,
    address: null,
    notes: null,
    createdBy: DEMO_USER_DEFINITIONS.admin.id,
    createdAt: now(),
    modifiedBy: DEMO_USER_DEFINITIONS.admin.id,
    modifiedAt: now(),
    tenantId: DEMO_TENANT_ID,
  }));
}

function buildFixtureContactPeople() {
  return [];
}

function buildFixtureUsers() {
  return [...Object.values(DEMO_USER_DEFINITIONS), ...DEMO_ADDITIONAL_TENANT_USERS].map((definition) => ({
    id: definition.id,
    email: definition.email,
    name: definition.name,
    role: definition.role,
    supabase_id: null,
    tenantId: DEMO_TENANT_ID,
    lastLoginAt: null,
  }));
}

function buildFixtureSuppliers() {
  return [
    {
      id: 1,
      name: DEMO_SUPPLIER_DEFINITION.name,
      contactPerson: DEMO_CONTACT_PERSON,
      email: DEMO_SUPPLIER_DEFINITION.email,
      phone: DEMO_CONTACT_PHONES.primary,
      address: null,
      notes: null,
      createdBy: DEMO_USER_DEFINITIONS.admin.id,
      createdAt: now(),
      modifiedBy: DEMO_USER_DEFINITIONS.admin.id,
      modifiedAt: now(),
      tenantId: DEMO_TENANT_ID,
    },
  ];
}

function buildFixturePurchasers() {
  return [
    {
      id: 1,
      name: DEMO_PURCHASER_DEFINITION.name,
      contactPerson: DEMO_CONTACT_PERSON,
      email: DEMO_PURCHASER_DEFINITION.email,
      phone: DEMO_CONTACT_PHONES.secondary,
      address: null,
      notes: null,
      createdBy: DEMO_USER_DEFINITIONS.admin.id,
      createdAt: now(),
      modifiedBy: DEMO_USER_DEFINITIONS.admin.id,
      modifiedAt: now(),
      tenantId: DEMO_TENANT_ID,
    },
  ];
}

function buildFixtureInventoryTransactions() {
  return DEMO_TRANSACTION_DEFINITIONS.map((definition, index) => ({
    id: index + 1,
    chemicalId: CHEMICAL_IDS[definition.chemical],
    transactionType: definition.transactionType,
    inventoryApplied: true,
    quantity: definition.quantity,
    finalCountedAmount: null,
    unit: definition.unit,
    transactionDate: now(),
    supplierId: null,
    supplierContactId: definition.party === CONTACT_TYPES.supplier ? CONTACT_IDS[definition.contact as DemoContactKey] : null,
    purchaserId: null,
    purchaserContactId: definition.party === CONTACT_TYPES.purchaser ? CONTACT_IDS[definition.contact as DemoContactKey] : null,
    price: definition.extras.price,
    referenceNumber: definition.extras.referenceNumber,
    supplierBatchNumber: null,
    usageDetails: null,
    disposalReason: null,
    notes: definition.extras.notes,
    createdBy: DEMO_USER_DEFINITIONS.admin.id,
    createdAt: now(),
    tenantId: DEMO_TENANT_ID,
  }));
}

function buildFixtureOrders() {
  return DEMO_ORDER_DEFINITIONS.map((definition: DemoOrderDefinition, index) => ({
    id: index + 1,
    chemicalId: CHEMICAL_IDS[definition.chemical as DemoChemicalKey],
    chemicalName: null,
    casNumber: null,
    contactId: CONTACT_IDS[definition.contact as DemoContactKey],
    direction: definition.direction,
    quantity: definition.quantity,
    unit: definition.unit,
    pricePerItem: definition.pricePerItem,
    externalProductNumber: definition.externalProductNumber,
    referenceNumber: definition.referenceNumber,
    orderStatus: definition.orderStatus,
    submittedBy: DEMO_USER_DEFINITIONS[definition.requestedBy].id,
    submittedAt: definition.submittedAt,
    requestedBy: DEMO_USER_DEFINITIONS[definition.requestedBy].id,
    orderedBy: definition.orderedAt ? DEMO_USER_DEFINITIONS[definition.requestedBy].id : null,
    orderedAt: definition.orderedAt ?? null,
    deliverySchedule: definition.deliverySchedule ?? defaultDeliverySchedule(definition.quantity),
    arrivedDate: definition.arrivedDate ?? null,
    notes: definition.notes,
    internalBatchNumber: definition.internalBatchNumber,
    externalBatchNumber: definition.externalBatchNumber ?? null,
    cancellationReason: definition.cancellationReason ?? null,
    returnedAt: null,
    returnNotes: null,
    tenantId: DEMO_TENANT_ID,
  }));
}

function buildFixtureChangeHistory() {
  return DEMO_CHANGE_HISTORY_DEFINITIONS.map((definition) => ({
    id: definition.id,
    chemicalId: CHEMICAL_IDS[definition.chemical],
    userId: DEMO_USER_DEFINITIONS[definition.user].id,
    changeDate: now(),
    changeType: definition.changeType,
    fieldName: definition.fieldName,
    oldValue: definition.oldValue,
    newValue: definition.newValue,
    transactionId: null,
    tenantId: DEMO_TENANT_ID,
  }));
}

const orderDeliverySchema = z.object({
  deliverySchedule: deliveryScheduleSchema,
});
const createOrderDeliverySchema = z.preprocess(rejectLegacyOrderFields, orderDeliverySchema.extend({
  direction: orderDirectionSchema.default(ORDER_DIRECTIONS.purchase),
  orderStatus: orderStatusSchema.default(ORDER_STATUSES.requested),
}));

const TRANSACTION_TYPE_FIELD = insertInventoryTransactionSchema.keyof().enum.transactionType;

function reclassificationTarget(transaction: TransactionFixture, patch: JsonObject): TransactionType | null {
  const to = patch[TRANSACTION_TYPE_FIELD];
  return typeof to === "string" && isTransactionType(to) && isReclassification(transaction.transactionType, to) ? to : null;
}

function reclassificationRefusal(transaction: TransactionFixture, patch: JsonObject): string | null {
  const to = reclassificationTarget(transaction, patch);
  if (!to) return null;
  const supplierContactId = (patch.supplierContactId ?? transaction.supplierContactId ?? null) as number | null;
  const transactionDate = (patch.transactionDate ?? transaction.transactionDate) as string | undefined;
  const [issue] = reclassificationIssues({ from: transaction.transactionType, to, role: DEMO_USER_DEFINITIONS.admin.role, supplierContactId, transactionDate });
  return issue?.message ?? null;
}

function amendedTransactionFields(transaction: TransactionFixture, patch: JsonObject): JsonObject {
  const reclassifying = reclassificationTarget(transaction, patch) != null;
  const fields: string[] = [...amendmentFieldsFor(transaction.inventoryApplied, reclassifying), ...(reclassifying ? [TRANSACTION_TYPE_FIELD] : [])];
  return Object.fromEntries(fields.filter((field) => field in patch).map((field) => [field, patch[field]]));
}

function increasedAmount(amount: DecimalValue, body: JsonObject): DecimalValue {
  return decimalAdd(amount, decimalValue(body.quantity));
}

function decreasedAmount(amount: DecimalValue, body: JsonObject): DecimalValue {
  return decimalSubtract(amount, decimalValue(body.quantity));
}

function countedAmount(amount: DecimalValue, body: JsonObject): DecimalValue {
  return decimalValue(body.finalCountedAmount, amount);
}

const STOCK_EFFECT_AMOUNTS: Record<TransactionStockEffect, (amount: DecimalValue, body: JsonObject) => DecimalValue> = {
  [TRANSACTION_STOCK_EFFECTS.increase]: increasedAmount,
  [TRANSACTION_STOCK_EFFECTS.decrease]: decreasedAmount,
  [TRANSACTION_STOCK_EFFECTS.computed]: countedAmount,
};

const ORDER_ACTION_PATH = new RegExp(`^${API_PATHS.orders}/(\\d+)/(${ORDER_ACTION_VALUES.join("|")})$`);

function placeOrder(order: OrderFixture): OrderHistoryEventType {
  Object.assign(order, { orderStatus: ORDER_STATUSES.ordered, orderedBy: DEMO_USER_DEFINITIONS.admin.id, orderedAt: now() });
  return ORDER_HISTORY_EVENTS.placed;
}

function fulfillOrder(order: OrderFixture, body: JsonObject): OrderHistoryEventType {
  const arrivedDate = typeof body.arrivedDate === "string" ? body.arrivedDate : now();
  Object.assign(order, { orderStatus: ORDER_STATUSES.fulfilled, arrivedDate, externalBatchNumber: nullableString(body.externalBatchNumber) });
  return ORDER_HISTORY_EVENTS.fulfilled;
}

function undoFulfillment(order: OrderFixture): OrderHistoryEventType {
  Object.assign(order, { orderStatus: ORDER_STATUSES.ordered, arrivedDate: null, externalBatchNumber: null });
  return ORDER_HISTORY_EVENTS.reopened;
}

function cancelOrder(order: OrderFixture, body: JsonObject): OrderHistoryEventType {
  Object.assign(order, { orderStatus: ORDER_STATUSES.cancelled, cancellationReason: nullableString(body.cancellationReason) });
  return ORDER_HISTORY_EVENTS.cancelled;
}

const ORDER_ACTION_HANDLERS: Record<OrderAction, (order: OrderFixture, body: JsonObject) => OrderHistoryEventType> = {
  [ORDER_ACTIONS.place]: placeOrder,
  [ORDER_ACTIONS.fulfill]: fulfillOrder,
  [ORDER_ACTIONS.undo]: undoFulfillment,
  [ORDER_ACTIONS.cancel]: cancelOrder,
};

function deliveryFields(deliverySchedule: DeliveryEntry[]) {
  return { deliverySchedule, quantity: orderQuantityOf(deliverySchedule) };
}

function demoMovedTotals(orders: readonly OrderFixture[]): OrderTotalRow[] {
  return orders.filter((order) => order.orderStatus === ORDER_STATUSES.fulfilled).map((order) => ({ orderId: order.id, total: order.quantity }));
}

type FixtureTransportOptions = {
  maxCreatedRecordsPerCollection?: number;
};

type JsonObject = Record<string, unknown>;
type ChemicalFixture = JsonObject & {
  id: number;
  amount: DecimalValue;
  unit: string;
  modifiedDate: string;
};
type ContactFixture = JsonObject & { id: number; contactType: string };
type TransactionFixture = JsonObject & { id: number; chemicalId: number; transactionType: string; inventoryApplied: boolean };
type AuditFixture = JsonObject & { id: number; chemicalId: number };
type OrderFixture = JsonObject & {
  id: number;
  quantity: DecimalValue;
  deliverySchedule: DeliveryEntry[];
  contactId: number;
  orderStatus: OrderStatus;
  direction: OrderDirection;
  pricePerItem: string | number | null;
  submittedAt: string;
  orderedBy: number | null;
  orderedAt: string | null;
  arrivedDate: string | null;
  externalBatchNumber: string | null;
  cancellationReason: string | null;
};

function parseJsonBody(init?: RequestInit): JsonObject {
  if (typeof init?.body !== "string" || init.body.length === 0) return {};
  try {
    const parsed: unknown = JSON.parse(init.body);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed as JsonObject : {};
  } catch {
    return {};
  }
}

function numeric(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function decimalValue(value: unknown, fallback: DecimalValue = DECIMAL_ZERO): string {
  const parsed = decimalSchema.safeParse(value);
  return parsed.success ? parsed.data : String(fallback);
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function nextId(rows: Array<{ id: number }>): number {
  return Math.max(0, ...rows.map((row) => row.id)) + 1;
}

function demoWriteBlocked() {
  return errorResponse(STATIC_DEMO_MESSAGES.writeBlockedDescription, 403);
}

export function createFixtureTransport({
  maxCreatedRecordsPerCollection = 100,
}: FixtureTransportOptions = {}): typeof fetch {
  const chemicals = buildFixtureChemicals() as ChemicalFixture[];
  const contacts = buildFixtureContacts() as ContactFixture[];
  const contactPeople = buildFixtureContactPeople() as Array<JsonObject & { id: number; contactId: number }>;
  const orders = buildFixtureOrders() as OrderFixture[];
  const users = buildFixtureUsers();
  const suppliers = buildFixtureSuppliers();
  const purchasers = buildFixturePurchasers();
  const transactions = buildFixtureInventoryTransactions() as TransactionFixture[];
  const auditHistory = buildFixtureChangeHistory() as AuditFixture[];
  const orderHistory = new Map<number, Array<JsonObject & { id: number; eventDate: string }>>();
  const createdCounts = new Map<string, number>();
  const addresses = createDemoAddresses({ tenantId: DEMO_TENANT_ID, userId: DEMO_USER_DEFINITIONS.admin.id, reserve: (collection) => reserve(collection), now });

  function appendOrderHistory(orderId: number, eventType: OrderHistoryEventType, eventDate = now()) {
    const rows = orderHistory.get(orderId) ?? [];
    rows.push({
      id: nextId(rows),
      orderId,
      eventType,
      userId: DEMO_USER_DEFINITIONS.admin.id,
      eventDate,
      detail: null,
      tenantId: DEMO_TENANT_ID,
    });
    orderHistory.set(orderId, rows);
  }

  for (const order of orders) {
    appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.requested, order.submittedAt);
    if (order.orderedAt) appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.placed, order.orderedAt);
    if (order.orderStatus === ORDER_STATUSES.fulfilled && order.arrivedDate) appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.fulfilled, order.arrivedDate);
    if (order.orderStatus === ORDER_STATUSES.cancelled) appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.cancelled, order.orderedAt ?? order.submittedAt);
  }

  function reserve(collection: string, amount = 1): Response | null {
    const count = createdCounts.get(collection) ?? 0;
    if (count + amount > maxCreatedRecordsPerCollection) {
      return errorResponse(STATIC_DEMO_MESSAGES.recordLimitReached, 409);
    }
    createdCounts.set(collection, count + amount);
    return null;
  }

  function addAudit(chemicalId: number, changeType: string, fieldName: string | null, oldValue: string | null, newValue: string | null) {
    auditHistory.unshift({
      id: nextId(auditHistory),
      chemicalId,
      userId: DEMO_USER_DEFINITIONS.admin.id,
      changeDate: now(),
      changeType,
      fieldName,
      oldValue,
      newValue,
      transactionId: null,
      tenantId: DEMO_TENANT_ID,
    });
  }

  function applyTransactionToChemical(body: JsonObject) {
    if (body.inventoryApplied === false) return;
    const chemical = chemicals.find((row) => row.id === numeric(body.chemicalId));
    if (!chemical || typeof body.transactionType !== "string" || !isTransactionType(body.transactionType)) return;
    const metadata = getTransactionTypeMetadata(body.transactionType);
    const oldAmount = chemical.amount;
    chemical.amount = STOCK_EFFECT_AMOUNTS[metadata.stockEffect](chemical.amount, body);
    chemical.modifiedDate = now();
    addAudit(chemical.id, CHANGE_HISTORY_TYPES.UPDATED, CHANGE_HISTORY_FIELDS.amount, String(oldAmount), String(chemical.amount));
  }

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method ?? "GET").toUpperCase();
    const url = requestUrl(input);
    const path = url.pathname;
    const body = parseJsonBody(init);

    const dashboard = method === "GET" ? demoDashboardResponse(url, {
      chemicals, transactions, orders, contacts, contactPeople, users, auditHistory, orderHistory,
    }) : null;
    if (dashboard) return jsonResponse(dashboard.body, dashboard.status);

    if (method === "GET" && path === API_PATHS.orders) {
      const result = demoOrderPriceResponse(url, attachMovedQuantities(orders, demoMovedTotals(orders)));
      return jsonResponse(result.body, result.status);
    }

    const staticGets: Record<string, () => unknown> = {
      [API_PATHS.chemicals]: () => chemicals,
      [API_PATHS.contacts]: () => contacts,
      [API_PATHS.contactPeople]: () => contactPeople,
      [API_PATHS.contactLastOrderDates]: () => latestOrderDatePerContact(orders),
      [API_PATHS.users]: () => users,
      [API_PATHS.suppliers]: () => suppliers,
      [API_PATHS.purchasers]: () => purchasers,
      [API_PATHS.inventoryTransactions]: () => transactions,
      [API_PATHS.audit]: () => auditHistory,
      [API_PATHS.documentGeneration]: () => DEMO_GENERATION_CONFIG,
      [`${API_PATHS.documentGeneration}/config`]: () => DEMO_GENERATION_CONFIG,
      [`${API_PATHS.orders}/import-references`]: () => ({
        suppliers: contacts
          .filter((contact) => canActAsSupplier(contact))
          .map((contact) => contact.company)
          .sort(),
        purchasers: contacts
          .filter((contact) => canActAsPurchaser(contact))
          .map((contact) => contact.company)
          .sort(),
        requestedByEmails: users.map((user) => user.email).filter(Boolean).sort(),
      }),
    };

    if (method === "GET" && staticGets[path]) return jsonResponse(staticGets[path]());
    if (method === "POST" && path === `${API_PATHS.orders}/import-preview`) {
      return jsonResponse(previewOrderImport(typeof body.csv === "string" ? body.csv : "", {
        suppliers: contacts
          .filter((contact) => canActAsSupplier(contact))
          .filter((contact) => typeof contact.company === "string")
          .map((contact) => ({ id: contact.id, name: contact.company as string })),
        purchasers: contacts
          .filter((contact) => canActAsPurchaser(contact))
          .filter((contact) => typeof contact.company === "string")
          .map((contact) => ({ id: contact.id, name: contact.company as string })),
        users: users.filter((user) => Boolean(user.email)).map((user) => ({ id: user.id, email: user.email! })),
        chemicals: chemicals
          .filter((chemical) => typeof chemical.name === "string" && typeof chemical.casNumber === "string")
          .map((chemical) => ({ id: chemical.id, name: chemical.name as string, casNumber: chemical.casNumber as string })),
      }));
    }

    const chemicalMatch = path.match(routePattern(`${API_PATHS.chemicals}/:id`));
    const chemicalHistoryMatch = path.match(routePattern(`${API_PATHS.chemicals}/:id/history`));
    const chemicalTransactionsMatch = path.match(routePattern(`${API_PATHS.chemicals}/:id/transactions`));
    if (method === "GET" && chemicalMatch) {
      const chemical = chemicals.find((row) => row.id === Number(chemicalMatch[1]));
      return chemical ? jsonResponse(chemical) : errorResponse(NOT_FOUND_MESSAGES.chemical, 404);
    }
    if (method === "GET" && chemicalHistoryMatch) {
      return jsonResponse(auditHistory.filter((row) => row.chemicalId === Number(chemicalHistoryMatch[1])));
    }
    if (method === "GET" && chemicalTransactionsMatch) {
      return jsonResponse(transactions.filter((row) => row.chemicalId === Number(chemicalTransactionsMatch[1])));
    }

    if (path === API_PATHS.chemicals && method === "POST") {
      const initialAmount = decimalValue(body.initialAmount);
      const initialTransactionType = INITIAL_STOCK_TYPE_VALUES.find((value) => value === (body.initialTransactionType ?? DEFAULT_INITIAL_STOCK_TYPE));
      if (!initialTransactionType) return errorResponse(INITIAL_STOCK_MESSAGES.typeInvalid, 400);
      const details = (typeof body.openingBalance === "object" && body.openingBalance !== null ? body.openingBalance : {}) as JsonObject;
      const supplierContactId = numeric(details.supplierContactId) || null;
      const initialTransactionDate = nullableString(body.initialTransactionDate) ?? undefined;
      const issues = initialStockIssues({ initialAmount, initialTransactionType, initialTransactionDate, openingBalance: { supplierContactId } });
      if (issues.length) return errorResponse(issues[0].message, 400);
      if (decimalCompare(initialAmount, 0) > 0 && supplierContactId) {
        const supplier = contacts.find((contact) => contact.id === supplierContactId);
        if (!supplier) return errorResponse(TRANSACTION_VALIDATION_MESSAGES.supplierNotFound, 400);
        if (!canActAsSupplier(supplier)) {
          return errorResponse(TRANSACTION_VALIDATION_MESSAGES.supplierInvalid, 400);
        }
      }
      const limited = reserve("chemicals");
      if (limited) return limited;
      const id = nextId(chemicals);
      const chemical = {
        id,
        productNumber: nullableString(body.productNumber),
        name: String(body.name ?? ""),
        casNumber: String(body.casNumber ?? ""),
        batchNumber: String(body.batchNumber ?? ""),
        location: String(body.location ?? ""),
        amount: initialAmount,
        unit: String(body.unit ?? ""),
        costPerUnit: nullableString(body.costPerUnit),
        modifiedBy: DEMO_USER_DEFINITIONS.admin.id,
        modifiedDate: now(),
        tenantId: DEMO_TENANT_ID,
        parentChemicalId: null,
        splitTransactionId: null,
      };
      chemicals.unshift(chemical);
      addAudit(id, CHANGE_HISTORY_TYPES.CREATED, null, null, null);
      if (decimalCompare(initialAmount, 0) > 0) {
        const transaction = {
          id: nextId(transactions),
          chemicalId: id,
          transactionType: initialTransactionType,
          inventoryApplied: getTransactionTypeMetadata(initialTransactionType).inventoryAppliedDefault,
          quantity: initialAmount,
          finalCountedAmount: null,
          unit: chemical.unit,
          transactionDate: initialTransactionType === INITIAL_STOCK_TYPES.purchase ? parseInitialPurchaseDate(initialTransactionDate)!.toISOString() : now(),
          supplierId: null,
          supplierContactId,
          purchaserId: null,
          purchaserContactId: null,
          price: nullableString(details.price),
          referenceNumber: nullableString(details.referenceNumber),
          supplierBatchNumber: nullableString(details.supplierBatchNumber),
          usageDetails: null,
          disposalReason: null,
          notes: null,
          createdBy: DEMO_USER_DEFINITIONS.admin.id,
          createdAt: now(),
          tenantId: DEMO_TENANT_ID,
        };
        transactions.unshift(transaction);
      }
      return jsonResponse(chemical, 201);
    }
    if (chemicalMatch && method === "PUT") {
      const index = chemicals.findIndex((row) => row.id === Number(chemicalMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.chemical, 404);
      const original = chemicals[index];
      const edits = chemicalEditInputSchema.safeParse(body);
      if (!edits.success) return errorResponse(edits.error.issues[0].message, 400);
      const updated = { ...original, ...edits.data, modifiedBy: DEMO_USER_DEFINITIONS.admin.id, modifiedDate: now(), tenantId: DEMO_TENANT_ID };
      chemicals[index] = updated;
      addAudit(original.id, CHANGE_HISTORY_TYPES.UPDATED, null, null, null);
      return jsonResponse(updated);
    }
    if (chemicalMatch && method === "DELETE") {
      const index = chemicals.findIndex((row) => row.id === Number(chemicalMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.chemical, 404);
      chemicals.splice(index, 1);
      return new Response(null, { status: 204 });
    }

    const splitMatch = path.match(routePattern(`${API_PATHS.chemicals}/:id/split`));
    if (splitMatch && method === "POST") {
      const parent = chemicals.find((row) => row.id === Number(splitMatch[1]));
      if (!parent) return errorResponse(NOT_FOUND_MESSAGES.chemical, 404);
      const split = splitChemicalSchema.safeParse(body);
      if (!split.success) return errorResponse(split.error.issues[0].message, 400);
      const overdraw = splitOverdrawIssue(split.data.children, parent.amount);
      if (overdraw) return errorResponse(overdraw, 400);
      const childrenInput = split.data.children;
      const limited = reserve("chemicals", childrenInput.length);
      if (limited) return limited;
      const children = childrenInput.map((child) => {
        const created = {
          ...parent,
          id: nextId(chemicals),
          batchNumber: String(child.batchNumber ?? ""),
          amount: decimalValue(child.quantity),
          parentChemicalId: parent.id,
          modifiedDate: now(),
        };
        chemicals.unshift(created);
        return created;
      });
      parent.amount = decimalSubtract(parent.amount, decimalSum(children.map((child) => child.amount)));
      parent.modifiedDate = now();
      return jsonResponse({ parent, children }, 201);
    }

    const transactionMatch = path.match(routePattern(`${API_PATHS.inventoryTransactions}/:id`));
    const transactionMetadataMatch = path.match(routePattern(`${API_PATHS.inventoryTransactions}/:id/metadata`));
    const transactionHistoryMatch = path.match(routePattern(`${API_PATHS.inventoryTransactions}/:id/history`));
    if (method === "GET" && transactionMatch) {
      const transaction = transactions.find((row) => row.id === Number(transactionMatch[1]));
      return transaction ? jsonResponse(transaction) : errorResponse(NOT_FOUND_MESSAGES.transaction, 404);
    }
    if (method === "GET" && transactionHistoryMatch) return jsonResponse([]);
    if (path === API_PATHS.inventoryTransactions && method === "POST") {
      const input = transactionWorkflowSchema.safeParse({ ...body, createdBy: DEMO_USER_DEFINITIONS.admin.id, tenantId: DEMO_TENANT_ID });
      if (!input.success) return errorResponse(input.error.issues[0].message, 400);
      const limited = reserve("transactions");
      if (limited) return limited;
      const transaction = {
        id: nextId(transactions),
        chemicalId: numeric(body.chemicalId),
        transactionType: String(body.transactionType ?? ""),
        inventoryApplied: body.inventoryApplied !== false,
        quantity: body.quantity == null ? null : decimalValue(body.quantity),
        finalCountedAmount: body.finalCountedAmount == null ? null : decimalValue(body.finalCountedAmount),
        unit: String(body.unit ?? ""),
        transactionDate: typeof body.transactionDate === "string" ? body.transactionDate : now(),
        supplierId: null,
        supplierContactId: body.supplierContactId == null ? null : numeric(body.supplierContactId),
        purchaserId: null,
        purchaserContactId: body.purchaserContactId == null ? null : numeric(body.purchaserContactId),
        price: nullableString(body.price),
        referenceNumber: nullableString(body.referenceNumber),
        supplierBatchNumber: nullableString(body.supplierBatchNumber),
        usageDetails: nullableString(body.usageDetails),
        disposalReason: nullableString(body.disposalReason),
        notes: nullableString(body.notes),
        createdBy: DEMO_USER_DEFINITIONS.admin.id,
        createdAt: now(),
        tenantId: DEMO_TENANT_ID,
      };
      transactions.unshift(transaction);
      applyTransactionToChemical(transaction);
      return jsonResponse(transaction, 201);
    }
    if (transactionMetadataMatch && method === "PATCH") {
      const index = transactions.findIndex((row) => row.id === Number(transactionMetadataMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.transaction, 404);
      const patch = transactionMetadataInputSchema.safeParse(body);
      if (!patch.success) return errorResponse(patch.error.issues[0].message, 400);
      const refusal = reclassificationRefusal(transactions[index], patch.data);
      if (refusal) return errorResponse(refusal, 400);
      transactions[index] = { ...transactions[index], ...amendedTransactionFields(transactions[index], patch.data) };
      return jsonResponse(transactions[index]);
    }
    if (transactionMatch && method === "DELETE") {
      const index = transactions.findIndex((row) => row.id === Number(transactionMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.transaction, 404);
      transactions.splice(index, 1);
      return new Response(null, { status: 204 });
    }

    const addressResponse = addresses.respond(method, path, body);
    if (addressResponse) return addressResponse;

    const contactMatch = path.match(routePattern(`${API_PATHS.contacts}/:id`));
    const contactPeopleMatch = path.match(routePattern(`${API_PATHS.contacts}/:id/people`));
    const contactPersonMatch = path.match(routePattern(`${API_PATHS.contacts}/:id/people/:id`));
    if (method === "GET" && contactMatch) {
      const contact = contacts.find((row) => row.id === Number(contactMatch[1]));
      return contact ? jsonResponse(contact) : errorResponse(NOT_FOUND_MESSAGES.contact, 404);
    }
    if (method === "GET" && contactPeopleMatch) {
      return jsonResponse(contactPeople.filter((row) => row.contactId === Number(contactPeopleMatch[1])));
    }
    if (path === API_PATHS.contacts && method === "POST") {
      const limited = reserve("contacts");
      if (limited) return limited;
      const contact = {
        id: nextId(contacts),
        company: String(body.company ?? ""),
        contactPerson: DEMO_CONTACT_PERSON,
        contactType: String(body.contactType ?? CONTACT_TYPES.supplier),
        email: nullableString(body.email),
        phone: nullableString(body.phone),
        address: nullableString(body.address),
        notes: nullableString(body.notes),
        createdBy: DEMO_USER_DEFINITIONS.admin.id,
        createdAt: now(),
        modifiedBy: DEMO_USER_DEFINITIONS.admin.id,
        modifiedAt: now(),
        tenantId: DEMO_TENANT_ID,
      };
      contacts.unshift(contact);
      addresses.addInitial({ ...contact, country: nullableString(body.country) });
      return jsonResponse(contact, 201);
    }
    if (contactMatch && method === "PUT") {
      const index = contacts.findIndex((row) => row.id === Number(contactMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.contact, 404);
      const edits = insertContactSchema.safeParse({ ...body, createdBy: contacts[index].createdBy, modifiedBy: DEMO_USER_DEFINITIONS.admin.id, tenantId: DEMO_TENANT_ID });
      if (!edits.success) return errorResponse(edits.error.issues[0].message, 400);
      contacts[index] = { ...contacts[index], ...edits.data, id: contacts[index].id, modifiedAt: now() };
      return jsonResponse(contacts[index]);
    }
    if (contactMatch && method === "DELETE") {
      const index = contacts.findIndex((row) => row.id === Number(contactMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.contact, 404);
      contacts.splice(index, 1);
      return new Response(null, { status: 204 });
    }
    if (contactPeopleMatch && method === "POST") {
      const limited = reserve("contactPeople");
      if (limited) return limited;
      const input = insertContactPersonSchema.safeParse({ ...body, contactId: Number(contactPeopleMatch[1]), tenantId: DEMO_TENANT_ID });
      if (!input.success) return errorResponse(input.error.issues[0].message, 400);
      const person = { ...input.data, id: nextId(contactPeople) };
      contactPeople.unshift(person);
      return jsonResponse(person, 201);
    }
    if (contactPersonMatch && method === "PUT") {
      const index = contactPeople.findIndex((row) => row.id === Number(contactPersonMatch[2]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.contactPerson, 404);
      const edits = insertContactPersonSchema.partial().safeParse({ ...body, contactId: Number(contactPersonMatch[1]), tenantId: DEMO_TENANT_ID });
      if (!edits.success) return errorResponse(edits.error.issues[0].message, 400);
      contactPeople[index] = { ...contactPeople[index], ...edits.data, id: contactPeople[index].id };
      return jsonResponse(contactPeople[index]);
    }
    if (contactPersonMatch && method === "DELETE") {
      const index = contactPeople.findIndex((row) => row.id === Number(contactPersonMatch[2]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.contactPerson, 404);
      contactPeople.splice(index, 1);
      return new Response(null, { status: 204 });
    }

    const orderMatch = path.match(routePattern(`${API_PATHS.orders}/:id`));
    const orderHistoryMatch = path.match(routePattern(`${API_PATHS.orders}/:id/history`));
    const orderActionMatch = path.match(ORDER_ACTION_PATH);
    if (method === "GET" && orderMatch) {
      const order = orders.find((row) => row.id === Number(orderMatch[1]));
      return order ? jsonResponse(order) : errorResponse(NOT_FOUND_MESSAGES.order, 404);
    }
    if (method === "GET" && orderHistoryMatch) {
      const rows = orderHistory.get(Number(orderHistoryMatch[1])) ?? [];
      return jsonResponse([...rows].sort((a, b) => b.eventDate.localeCompare(a.eventDate) || b.id - a.id));
    }
    if (path === API_PATHS.orders && method === "POST") {
      const lines = createOrderDeliverySchema.safeParse(body);
      if (!lines.success) return errorResponse(lines.error.issues[0].message, 400);
      const limited = reserve("orders");
      if (limited) return limited;
      const order = {
        id: nextId(orders),
        chemicalId: body.chemicalId == null ? null : numeric(body.chemicalId),
        chemicalName: nullableString(body.chemicalName),
        casNumber: nullableString(body.casNumber),
        contactId: numeric(body.contactId),
        direction: lines.data.direction,
        ...deliveryFields(lines.data.deliverySchedule),
        unit: String(body.unit ?? ""),
        pricePerItem: nullableString(body.pricePerItem),
        externalProductNumber: nullableString(body.externalProductNumber),
        referenceNumber: nullableString(body.referenceNumber),
        customerPoNumber: nullableString(body.customerPoNumber),
        contactAddressId: typeof body.contactAddressId === "number" ? body.contactAddressId : null,
        orderStatus: lines.data.orderStatus,
        submittedBy: DEMO_USER_DEFINITIONS.admin.id,
        submittedAt: typeof body.submittedAt === "string" ? body.submittedAt : now(),
        requestedBy: numeric(body.requestedBy),
        orderedBy: body.orderedAt ? DEMO_USER_DEFINITIONS.admin.id : null,
        orderedAt: nullableString(body.orderedAt),
        arrivedDate: nullableString(body.arrivedDate),
        notes: nullableString(body.notes),
        internalBatchNumber: nullableString(body.internalBatchNumber),
        externalBatchNumber: null,
        cancellationReason: null,
        returnedAt: null,
        returnNotes: null,
        tenantId: DEMO_TENANT_ID,
      };
      orders.unshift(order);
      appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.requested, order.submittedAt);
      if (order.orderedAt) appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.placed, order.orderedAt);
      if (order.orderStatus === ORDER_STATUSES.fulfilled && order.arrivedDate) appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.fulfilled, order.arrivedDate);
      if (order.orderStatus === ORDER_STATUSES.cancelled) appendOrderHistory(order.id, ORDER_HISTORY_EVENTS.cancelled, order.orderedAt ?? order.submittedAt);
      return jsonResponse(order, 201);
    }
    if (orderMatch && method === "PUT") {
      const parsed = editOrderSchema.safeParse(body);
      if (!parsed.success) return errorResponse(parsed.error.issues[0].message, 400);
      const index = orders.findIndex((row) => row.id === Number(orderMatch[1]));
      if (index < 0) return errorResponse(NOT_FOUND_MESSAGES.order, 404);
      if (!canAmendOrderStatus(orders[index].orderStatus)) return errorResponse(ORDER_INPUT_COPY.editStatus, 409);
      const { deliverySchedule, quantity: _quantity, ...edits } = parsed.data;
      const values = deliveryFields(deliverySchedule ?? orders[index].deliverySchedule);
      const merged = { ...orders[index], ...edits, ...values, id: orders[index].id, tenantId: DEMO_TENANT_ID };
      const issue = firstOrderInputIssue({ ...merged, submittedAt: merged.submittedAt ? new Date(String(merged.submittedAt)) : null } as SemanticOrderInput);
      if (issue) return errorResponse(issue, 400);
      orders[index] = merged;
      appendOrderHistory(orders[index].id, ORDER_HISTORY_EVENTS.amended);
      return jsonResponse(orders[index]);
    }
    if (orderActionMatch && method === "POST") {
      const order = orders.find((row) => row.id === Number(orderActionMatch[1]));
      if (!order) return errorResponse(NOT_FOUND_MESSAGES.order, 404);
      const historyEvent = ORDER_ACTION_HANDLERS[orderActionMatch[2] as OrderAction](order, body);
      appendOrderHistory(order.id, historyEvent, historyEvent === ORDER_HISTORY_EVENTS.placed ? String(order.orderedAt) : undefined);
      return jsonResponse(order);
    }

    if (method !== "GET") return demoWriteBlocked();
    return errorResponse(STATIC_DEMO_MESSAGES.noFixtureData(path), 404);
  };
}

export const fixtureTransport = createFixtureTransport();
